import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Trophy, ChevronRight, ChevronDown, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getMedalStyle } from '@/lib/medals';
import { useAuth } from '@/context/AuthContext';
import { GET_LIVE_LEADERBOARD } from '@/graphql/queries';
import { BonusType, Gender, type GetLiveLeaderboardQuery } from '@/gql/graphql';
import { ScoreBadge } from '@/features/races/detail/ScoreBadge';

const POLL_INTERVAL = 3 * 60 * 1000;
const PREVIEW_COUNT = 3;

type Breakdown = NonNullable<NonNullable<GetLiveLeaderboardQuery['liveLeaderboard']>['currentUserBreakdown']>;
type BreakdownRunner = NonNullable<Breakdown['runners']>[number];

const BONUS_LABELS: Record<string, string> = {
    [BonusType.Top_3]: 'Top 3',
    [BonusType.Top_5]: 'Top 5',
    [BonusType.Top_10]: 'Top 10',
    [BonusType.Dnf_3]: 'DNF x3',
    [BonusType.Dnf_5]: 'DNF x5',
};

function genderLabel(gender: Gender | null | undefined): string {
    if (gender === Gender.Male) return '♂';
    if (gender === Gender.Female) return '♀';
    return '';
}

interface LiveLeaderboardWidgetProps {
    raceId: string;
}

export function LiveLeaderboardWidget({ raceId }: LiveLeaderboardWidgetProps) {
    const { t } = useTranslation('races');
    const { user: currentUser } = useAuth();
    const previousScoreRef = useRef<number | null>(null);
    const [scoreDelta, setScoreDelta] = useState<number | null>(null);
    const [expanded, setExpanded] = useState(false);

    const { data } = useQuery(GET_LIVE_LEADERBOARD, {
        variables: { input: { raceId } },
        pollInterval: POLL_INTERVAL,
    });

    const entries = data?.liveLeaderboard?.entries ?? [];
    const breakdown = data?.liveLeaderboard?.currentUserBreakdown;
    const currentUserIndex = entries.findIndex(e => e.user?.id === currentUser?.id);
    const currentUserEntry = currentUserIndex >= 0 ? entries[currentUserIndex] : null;
    const currentUserRank = currentUserIndex >= 0 ? currentUserIndex + 1 : null;
    const currentScore = currentUserEntry?.totalScore ?? null;

    const topEntries = entries.slice(0, PREVIEW_COUNT);
    const showCurrentUserSeparately = currentUserRank != null && currentUserRank > PREVIEW_COUNT;

    const scoredRunners = entries[0]?.scoredRunners ?? 0;
    const totalRunners = entries[0]?.totalRunners ?? 0;

    const hasBreakdown = breakdown && (breakdown.runners?.length ?? 0) > 0;

    // Delta calculation
    useEffect(() => {
        if (currentScore == null) return;
        if (previousScoreRef.current != null) {
            const delta = Math.round((currentScore - previousScoreRef.current) * 10) / 10;
            if (delta !== 0) setScoreDelta(delta);
        }
        previousScoreRef.current = currentScore;
    }, [currentScore]);

    if (entries.length === 0) return null;

    return (
        <div className="rounded-2xl bg-emerald-50/50 p-4 border border-emerald-100">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Trophy size={18} className="text-emerald-500" />
                    <h3 className="text-sm font-bold text-emerald-800">{t('details.liveTracking.leaderboard.title')}</h3>
                </div>
                {scoredRunners > 0 && scoredRunners < totalRunners && (
                    <span className="text-[10px] text-emerald-400 font-medium">
                        {t('details.liveTracking.leaderboard.estimated', { scored: scoredRunners, total: totalRunners })}
                    </span>
                )}
            </div>

            {/* Current user score highlight (only if not already in top 3) */}
            {currentUserEntry && currentUserRank && currentUserRank > PREVIEW_COUNT && (
                <div className="mb-3">
                    <button
                        onClick={() => hasBreakdown && setExpanded(!expanded)}
                        className={cn(
                            "w-full bg-emerald-100/60 rounded-xl px-3 py-2.5 flex items-center justify-between",
                            hasBreakdown && "cursor-pointer hover:bg-emerald-100/80 transition-colors"
                        )}
                    >
                        <div className="flex items-center gap-2">
                            <div className={cn(
                                "w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-black flex-shrink-0",
                                getMedalStyle(currentUserRank)
                            )}>
                                {currentUserRank}
                            </div>
                            <span className="text-sm font-bold text-emerald-900">
                                {t('details.liveTracking.leaderboard.you')}
                            </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="text-lg font-black text-emerald-700">
                                {(currentScore ?? 0).toFixed(1)}
                            </span>
                            <span className="text-[10px] text-emerald-500 font-medium">
                                {t('details.liveTracking.leaderboard.pts')}
                            </span>
                            {scoreDelta != null && (
                                <span className={cn(
                                    "text-[10px] font-bold ml-0.5",
                                    scoreDelta > 0 ? "text-emerald-600" : "text-red-500"
                                )}>
                                    {scoreDelta > 0 ? `+${scoreDelta.toFixed(1)}` : scoreDelta.toFixed(1)}
                                </span>
                            )}
                            {hasBreakdown && (
                                <ChevronDown size={14} className={cn(
                                    "text-emerald-500 transition-transform ml-0.5",
                                    expanded && "rotate-180"
                                )} />
                            )}
                        </div>
                    </button>

                    {expanded && hasBreakdown && (
                        <BreakdownPanel breakdown={breakdown} />
                    )}
                </div>
            )}

            {/* Top 3 */}
            <div className="space-y-1.5">
                {topEntries.map((entry, index) => {
                    const rank = index + 1;
                    const isMe = entry.user?.id === currentUser?.id;
                    return (
                        <div key={entry.user?.id ?? index}>
                            <div
                                className={cn(
                                    "flex items-center gap-3 py-2 px-3 rounded-xl",
                                    isMe && "bg-emerald-100/50",
                                    isMe && hasBreakdown && "cursor-pointer hover:bg-emerald-100/70 transition-colors"
                                )}
                                onClick={isMe && hasBreakdown ? () => setExpanded(!expanded) : undefined}
                            >
                                <div className={cn(
                                    "w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0",
                                    getMedalStyle(rank)
                                )}>
                                    {rank}
                                </div>
                                <span className={cn(
                                    "flex-1 text-sm font-medium truncate",
                                    isMe ? "text-emerald-900 font-bold" : "text-slate-700"
                                )}>
                                    {entry.user?.username ?? '?'}
                                </span>
                                <span className="text-sm font-black text-slate-700 flex-shrink-0">
                                    {(entry.totalScore ?? 0).toFixed(1)}
                                </span>
                                <span className="text-[10px] text-slate-400 font-medium flex-shrink-0">
                                    {t('details.liveTracking.leaderboard.pts')}
                                </span>
                                {isMe && hasBreakdown && (
                                    <ChevronDown size={14} className={cn(
                                        "text-emerald-500 transition-transform flex-shrink-0",
                                        expanded && "rotate-180"
                                    )} />
                                )}
                            </div>
                            {isMe && expanded && hasBreakdown && (
                                <BreakdownPanel breakdown={breakdown} />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Current user if not in top 3 (separator row) */}
            {showCurrentUserSeparately && currentUserEntry && currentUserRank && (
                <>
                    <div className="border-t border-emerald-100 my-1.5" />
                    <div
                        className={cn(
                            "flex items-center gap-3 py-2 px-3 rounded-xl bg-emerald-100/50",
                            hasBreakdown && "cursor-pointer hover:bg-emerald-100/70 transition-colors"
                        )}
                        onClick={hasBreakdown ? () => setExpanded(!expanded) : undefined}
                    >
                        <div className={cn(
                            "w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0",
                            getMedalStyle(currentUserRank)
                        )}>
                            {currentUserRank}
                        </div>
                        <span className="flex-1 text-sm font-bold text-emerald-900 truncate">
                            {currentUserEntry.user?.username ?? '?'}
                        </span>
                        <span className="text-sm font-black text-slate-700 flex-shrink-0">
                            {(currentUserEntry.totalScore ?? 0).toFixed(1)}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium flex-shrink-0">
                            {t('details.liveTracking.leaderboard.pts')}
                        </span>
                        {hasBreakdown && (
                            <ChevronDown size={14} className={cn(
                                "text-emerald-500 transition-transform flex-shrink-0",
                                expanded && "rotate-180"
                            )} />
                        )}
                    </div>
                </>
            )}

            {/* Link to full leaderboard */}
            <Link
                to={`/races/${raceId}/live/leaderboard`}
                className="mt-3 flex items-center justify-center gap-1 text-sm font-bold text-emerald-600 hover:text-emerald-700 transition-colors pt-3 border-t border-emerald-100"
            >
                {t('details.liveTracking.leaderboard.viewFull')}
                <ChevronRight size={16} />
            </Link>
        </div>
    );
}

// ─── Breakdown Panel ───

function BreakdownPanel({ breakdown }: { breakdown: Breakdown }) {
    const { t } = useTranslation('races');
    const runners = breakdown.runners ?? [];
    const bonuses = breakdown.bonuses ?? [];

    const maleRunners = runners.filter(r => r.gender === Gender.Male);
    const femaleRunners = runners.filter(r => r.gender === Gender.Female);

    return (
        <div className="mt-1.5 px-2 pb-1 space-y-3 animate-in slide-in-from-top-1 duration-150">
            {/* Male runners */}
            {maleRunners.length > 0 && (
                <BreakdownGenderSection
                    label={`${t('details.liveTracking.leaderboard.breakdown.runners')} ♂`}
                    runners={maleRunners}
                />
            )}

            {/* Female runners */}
            {femaleRunners.length > 0 && (
                <BreakdownGenderSection
                    label={`${t('details.liveTracking.leaderboard.breakdown.runners')} ♀`}
                    runners={femaleRunners}
                />
            )}

            {/* Bonuses */}
            {bonuses.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                    {bonuses.map((b) => (
                        <span
                            key={`${b.type}-${b.gender}`}
                            className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                        >
                            <Award size={9} />
                            {BONUS_LABELS[b.type ?? ''] ?? b.type}
                            {b.gender && ` ${genderLabel(b.gender)}`}
                            <span className="text-amber-500 ml-0.5">+{b.points}</span>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function BreakdownGenderSection({ label, runners }: { label: string; runners: BreakdownRunner[] }) {
    // Sort: ranked by livePosition asc, then DNF/DNS/unranked at bottom
    const sorted = [...runners].sort((a, b) => {
        const aPos = a.livePosition ?? 9999;
        const bPos = b.livePosition ?? 9999;
        return aPos - bPos;
    });

    return (
        <div>
            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide mb-1">{label}</p>
            <div className="space-y-0.5">
                {sorted.map((r) => (
                    <BreakdownRunnerRow key={r.utmbUri} runner={r} />
                ))}
            </div>
        </div>
    );
}

function BreakdownRunnerRow({ runner }: { runner: BreakdownRunner }) {
    const predicted = runner.predictedPosition ?? 0;
    const live = runner.livePosition;
    const isDnf = runner.status === 'DNF';
    const isDns = runner.status === 'DNS';
    const isDoubled = (runner.tokens ?? 1) > 1;

    const positionLabel = predicted === 0 ? 'DNF' : `#${predicted}`;
    const liveLabel = isDns ? 'DNS' : isDnf ? 'DNF' : live != null && live > 0 ? `#${live}` : '—';

    return (
        <div className="flex items-center gap-2 py-0.5 px-1">
            {/* Predicted → Live */}
            <span className="text-[10px] text-slate-400 font-medium w-16 flex-shrink-0 text-right">
                {positionLabel} → {liveLabel}
            </span>

            {/* Score */}
            <ScoreBadge score={runner.score ?? 0} />

            {/* x2 badge */}
            {isDoubled && (
                <span className="px-1 py-0.5 rounded text-[9px] font-black bg-amber-100 text-amber-700">x2</span>
            )}
        </div>
    );
}
